import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Plus, Trash2, Edit2, Search, Layers, AlertTriangle, Image as ImageIcon, Upload, Loader2 } from 'lucide-react';
import { categoryService } from '../../components/services/categoryService';

export default function CategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal Thêm / Sửa
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [parentFilter, setParentFilter] = useState('all'); // 'all' | 'root' | 'sub'
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    gender: 'UNISEX',
    slug: '',
    image: '',
    parentId: '',
  });

  // Modal Xác nhận xóa
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // States phân trang (Pagination)
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getAllCategories();
      const list = Array.isArray(response) ? response : (response.data || []);
      setCategories(list);
    } catch (error) {
      console.error('Lỗi khi tải danh sách danh mục:', error);
      Swal.fire({
        title: 'Lỗi!',
        text: 'Không thể tải danh sách danh mục',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = (defaultParentId = '') => {
    setIsEditing(false);
    setFormData({
      id: null,
      name: '',
      gender: 'UNISEX',
      slug: '',
      image: '',
      parentId: defaultParentId || '',
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (cat) => {
    setIsEditing(true);
    setFormData({
      id: cat.id,
      name: cat.name || '',
      gender: cat.gender || 'UNISEX',
      slug: cat.slug || '',
      image: cat.image || '',
      parentId: cat.parentId ? String(cat.parentId) : '',
    });
    setShowModal(true);
  };

  const handleOpenDeleteModal = (cat) => {
    setCategoryToDelete(cat);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'name' && !isEditing) {
        updated.slug = value
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[đĐ]/g, 'd')
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-');
      }
      return updated;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        title: 'Cảnh báo!',
        text: 'Kích thước ảnh tối đa là 5MB',
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }

    setUploadingImage(true);
    try {
      const res = await categoryService.uploadImage(file);
      const data = res.data || res;
      if (data.success && data.url) {
        setFormData((prev) => ({ ...prev, image: data.url }));
        Swal.fire({
          title: 'Thành công!',
          text: 'Tải ảnh lên Cloudinary thành công! 🎉',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'Thất bại!',
          text: data.message || 'Lỗi khi upload ảnh',
          icon: 'error',
          confirmButtonColor: '#e11d48',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
    } catch (err) {
      console.error('Lỗi upload ảnh:', err);
      Swal.fire({
        title: 'Thất bại!',
        text: 'Không thể upload ảnh lên Cloudinary',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      Swal.fire({
        title: 'Chưa nhập tên!',
        text: 'Vui lòng nhập tên danh mục!',
        icon: 'warning',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        parentId: formData.parentId ? Number(formData.parentId) : null,
      };

      if (isEditing) {
        await categoryService.updateCategory(formData.id, payload);
        Swal.fire({
          title: 'Thành công!',
          text: 'Cập nhật danh mục thành công! 🎉',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      } else {
        await categoryService.createCategory(payload);
        Swal.fire({
          title: 'Thành công!',
          text: 'Thêm danh mục mới thành công! 🎉',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
          background: '#0f172a',
          color: '#ffffff'
        });
      }
      setShowModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Lỗi khi lưu danh mục:', error);
      Swal.fire({
        title: 'Thất bại!',
        text: 'Có lỗi xảy ra khi lưu danh mục',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    try {
      await categoryService.deleteCategory(categoryToDelete.id);
      Swal.fire({
        title: 'Đã xóa!',
        text: 'Đã xóa danh mục thành công!',
        icon: 'success',
        confirmButtonColor: '#4f46e5',
        background: '#0f172a',
        color: '#ffffff'
      });
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      console.error('Lỗi khi xóa danh mục:', error);
      Swal.fire({
        title: 'Không thể xóa!',
        text: 'Không thể xóa danh mục này (có thể do chứa sản phẩm)',
        icon: 'error',
        confirmButtonColor: '#e11d48',
        background: '#0f172a',
        color: '#ffffff'
      });
    }
  };

  // Lọc danh mục theo tìm kiếm và phân cấp
  const filteredCategories = categories.filter((cat) => {
    const nameMatch = (cat.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const genderMatch = (cat.gender || '').toLowerCase().includes(searchTerm.toLowerCase());
    const slugMatch = (cat.slug || '').toLowerCase().includes(searchTerm.toLowerCase());
    const parentMatch = (cat.parentName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSearch = nameMatch || genderMatch || slugMatch || parentMatch;

    if (!matchesSearch) return false;
    if (parentFilter === 'root') return !cat.parentId;
    if (parentFilter === 'sub') return !!cat.parentId;
    return true;
  });

  // Tính toán phân trang
  const totalPages = Math.ceil(filteredCategories.length / pageSize);
  const paginatedCategories = filteredCategories.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, parentFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* HEADER & ACTIONS */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800/80 backdrop-blur-xl">
        <div>
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
            Quản trị hệ thống
          </span>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-serif text-white mt-2 flex items-center gap-3">
            <Layers className="text-indigo-400 w-7 h-7" />
            Quản Lý Danh Mục
          </h1>
        </div>

        <button
          onClick={handleOpenAddModal}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-3 rounded-2xl text-xs font-bold shadow-lg shadow-indigo-600/25 transition-all cursor-pointer"
        >
          <Plus size={16} />
          <span>Thêm Danh Mục</span>
        </button>
      </div>

      {/* STATS OVERVIEW */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Tổng danh mục</span>
          <p className="text-2xl font-extrabold text-indigo-400 mt-1">{categories.length}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Danh mục Nam</span>
          <p className="text-2xl font-extrabold text-sky-400 mt-1">
            {categories.filter((c) => (c.gender || '').toUpperCase() === 'NAM').length}
          </p>
        </div>
        <div className="bg-slate-900 border border-slate-800/80 p-5 rounded-3xl shadow-xl">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Danh mục Nữ</span>
          <p className="text-2xl font-extrabold text-rose-400 mt-1">
            {categories.filter((c) => (c.gender || '').toUpperCase() === 'NỮ').length}
          </p>
        </div>
      </div>

      {/* SEARCH BAR & FILTERS */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
        <div className="relative min-w-[280px] flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Tìm kiếm theo tên, slug, danh mục cha..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-10 pr-4 py-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition shadow-inner"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 font-bold shrink-0">Phân loại:</span>
          <select
            value={parentFilter}
            onChange={(e) => setParentFilter(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 text-xs text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer"
          >
            <option value="all">Tất cả cấp bậc ({categories.length})</option>
            <option value="root">Chỉ danh mục chính (Cấp 1)</option>
            <option value="sub">Chỉ danh mục con (Cấp 2, 3)</option>
          </select>
        </div>
      </div>

      {/* CATEGORIES TABLE */}
      <div className="bg-slate-900 border border-slate-800/80 rounded-3xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/60 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="py-4 px-4 w-16 text-center">STT</th>
                <th className="py-4 px-4 w-20 text-center">Ảnh</th>
                <th className="py-4 px-6">Tên Danh Mục</th>
                <th className="py-4 px-5">Danh Mục Cha</th>
                <th className="py-4 px-4 w-28 text-center">Giới Tính</th>
                <th className="py-4 px-6">Slug</th>
                <th className="py-4 px-6 text-right whitespace-nowrap">Thao Tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-400 font-medium">
                    Đang tải danh sách danh mục...
                  </td>
                </tr>
              ) : paginatedCategories.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-400 font-medium">
                    Không tìm thấy danh mục nào.
                  </td>
                </tr>
              ) : (
                paginatedCategories.map((cat, idx) => {
                  const absoluteIndex = currentPage * pageSize + idx + 1;
                  return (
                    <tr key={cat.id || idx} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-4 px-4 text-center font-bold text-slate-400">{absoluteIndex}</td>
                      <td className="py-4 px-4 text-center">
                        <div
                          style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', maxWidth: '44px', maxHeight: '44px' }}
                          className="mx-auto rounded-2xl overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 shadow-sm"
                        >
                          {cat.image ? (
                            <img
                              src={cat.image}
                              alt={cat.name}
                              style={{ width: '100%', height: '100%' }}
                              className="w-full h-full object-cover block"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-full h-full flex items-center justify-center text-slate-500"
                            style={{ display: cat.image ? 'none' : 'flex' }}
                          >
                            <ImageIcon size={18} />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-bold text-white text-sm">{cat.name}</td>
                      <td className="py-4 px-5">
                        {cat.parentName ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-xl text-[11px] font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                            ↳ {cat.parentName}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-xl text-[10px] font-bold bg-slate-800 text-slate-400">
                            Cấp gốc (Cấp 1)
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-xl text-[10px] font-bold ${
                            (cat.gender || '').toUpperCase() === 'NAM'
                              ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20'
                              : (cat.gender || '').toUpperCase() === 'NỮ'
                              ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                              : 'bg-slate-700/30 text-slate-300 border border-slate-700'
                          }`}
                        >
                          {cat.gender || 'KHÁC'}
                        </span>
                      </td>
                      <td className="py-4 px-6 font-mono text-slate-400">{cat.slug || '-'}</td>
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEditModal(cat)}
                            className="p-2.5 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 transition cursor-pointer shadow-sm"
                            title="Chỉnh sửa"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(cat)}
                            className="p-2.5 rounded-2xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition cursor-pointer shadow-sm"
                            title="Xóa"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* THANH PHÂN TRANG (Pagination) */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-5 border-t border-slate-800 bg-slate-950/40">
            <span className="text-xs text-slate-400 font-medium">
              Trang <span className="font-bold text-white">{currentPage + 1}</span> / <span className="font-bold text-white">{totalPages}</span>
            </span>

            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                  currentPage === 0
                    ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                }`}
              >
                Trước
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(index)}
                  className={`w-9 h-9 rounded-xl text-xs font-bold border transition ${
                    currentPage === index
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                      : 'border-slate-700/80 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                  }`}
                >
                  {index + 1}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                  currentPage === totalPages - 1
                    ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                }`}
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL THÊM / SỬA DANH MỤC */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg p-8 space-y-6 shadow-2xl animate-in fade-in duration-200">
            <div className="flex justify-between items-center border-b border-slate-800 pb-4">
              <h3 className="text-base font-bold text-white font-serif">
                {isEditing ? 'Cập Nhật Danh Mục' : 'Thêm Danh Mục Mới'}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-white transition cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">
                  Tên danh mục <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Ví dụ: Áo Thun Nam, Váy Nữ..."
                  required
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">
                    Danh mục cha (Tùy chọn)
                  </label>
                  <select
                    name="parentId"
                    value={formData.parentId || ''}
                    onChange={handleFormChange}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition cursor-pointer"
                  >
                    <option value="">-- Là danh mục chính (Cấp 1) --</option>
                    {categories
                      .filter((c) => !isEditing || c.id !== formData.id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.parentName ? `↳ [${c.parentName}] ${c.name}` : `★ ${c.name}`}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Giới tính</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleFormChange}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition cursor-pointer"
                  >
                    <option value="UNISEX">Unisex</option>
                    <option value="NAM">Nam</option>
                    <option value="NỮ">Nữ</option>
                    <option value="TRẺ EM">Trẻ Em</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Slug (Đường dẫn)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleFormChange}
                  placeholder="ao-thun-nam (tự động tạo theo tên)"
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Ảnh đại diện danh mục</label>
                <div className="flex items-start gap-3">
                  <div
                    style={{ width: '56px', height: '56px', minWidth: '56px', minHeight: '56px', maxWidth: '56px', maxHeight: '56px' }}
                    className="rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center overflow-hidden shrink-0 shadow-inner"
                  >
                    {uploadingImage ? (
                      <Loader2 size={20} className="text-indigo-400 animate-spin" />
                    ) : formData.image ? (
                      <img
                        src={formData.image}
                        alt="Preview"
                        style={{ width: '100%', height: '100%' }}
                        className="w-full h-full object-cover block"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon size={22} className="text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <label
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                          uploadingImage
                            ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                            : 'bg-indigo-600/15 hover:bg-indigo-600/25 text-indigo-400 border border-indigo-500/30'
                        }`}
                      >
                        {uploadingImage ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Upload size={13} />
                        )}
                        <span>{uploadingImage ? 'Đang tải lên...' : 'Tải ảnh từ máy'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                      {formData.image && (
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}
                          className="text-xs text-rose-400 hover:text-rose-300 transition cursor-pointer font-bold"
                        >
                          Xóa ảnh
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      name="image"
                      value={formData.image}
                      onChange={handleFormChange}
                      placeholder="Hoặc dán URL ảnh trực tiếp (https://...)"
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-300 transition cursor-pointer"
                >
                  Hủy bỏ
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-lg shadow-indigo-600/30 transition cursor-pointer"
                >
                  {isEditing ? 'Lưu Thay Đổi' : 'Tạo Danh Mục'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL XÁC NHẬN XÓA */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-md p-8 space-y-6 shadow-2xl animate-in fade-in duration-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 shrink-0">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white font-serif">Xác nhận xóa danh mục</h3>
                <p className="text-xs text-slate-400 mt-1">
                  Bạn có chắc muốn xóa danh mục{' '}
                  <span className="text-white font-bold">"{categoryToDelete?.name}"</span> không?
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-300 transition cursor-pointer"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-6 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-lg shadow-rose-600/30"
              >
                <Trash2 size={15} /> Xóa ngay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}